import React, {useEffect, useState} from 'react';
import {Modal, Select} from '@mantine/core';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {UserStatusEnum, UserTypeEnum} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';
import {useGetPersonnelSelectOptionsQuery} from 'src/redux/queries/personnel';
import {useCreateSkillSignOffMutation} from 'src/redux/queries/skillSignOff';
import {useGetSkillListQuery} from 'src/redux/queries/skill';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface NewSignOffModalProps {
  opened: boolean;
  onClose: () => void;
}

/**
 * Standalone sign-off request — a supervisor asking for a practical
 * evaluation outside any course. Two distinct actions: "Create request"
 * leaves the pending request for the suggested evaluator to pick up from
 * their queue later; "Evaluate now" does the same create but goes straight
 * into the evaluation flow for an on-the-spot sign-off.
 */
export const NewSignOffModal: React.FC<NewSignOffModalProps> = ({
  opened,
  onClose,
}) => {
  const navigate = useAppNavigate();
  const {setAlert} = useAlert();

  const [userId, setUserId] = useState<string | null>(null);
  const [skillId, setSkillId] = useState<string | null>(null);

  const {data: personnel} = useGetPersonnelSelectOptionsQuery({
    start: 0,
    length: 10000,
    active: [UserStatusEnum.Active],
    userTypes: [UserTypeEnum.Contractor, UserTypeEnum.Employee],
    superviserOnly: false,
  });
  const {data: skills} = useGetSkillListQuery({start: 0, length: 1000});
  const [createSignOff, {isLoading}] = useCreateSkillSignOffMutation();

  useEffect(() => {
    if (!opened) {
      setUserId(null);
      setSkillId(null);
    }
  }, [opened]);

  const canSubmit = Boolean(userId && skillId) && !isLoading;

  const handleCreateRequest = async () => {
    if (!userId || !skillId) {
      return;
    }
    try {
      const response = await createSignOff({
        userId: Number(userId),
        skillId: Number(skillId),
      }).unwrap();
      setAlert(
        `A sign-off request for ${response.skillName} was created for ${response.userName}.`,
        'success'
      );
      onClose();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const handleEvaluateNow = async () => {
    if (!userId || !skillId) {
      return;
    }
    try {
      const response = await createSignOff({
        userId: Number(userId),
        skillId: Number(skillId),
      }).unwrap();
      onClose();
      navigate(PathUtility.TrainingSignOffEvaluate(String(response.id)));
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  const activeSkillOptions = (skills?.data ?? [])
    .filter(skill => skill.active)
    .map(skill => ({value: String(skill.id), label: skill.name}));

  return (
    <Modal opened={opened} onClose={onClose} title="New sign-off" centered>
      <Stack gap="md">
        <Select
          label="Trainee"
          placeholder="Select a person"
          required
          searchable
          data={(personnel?.data ?? []).map(option => ({
            value: String(option.id),
            label: option.label ?? '',
          }))}
          value={userId}
          comboboxProps={{withinPortal: true, zIndex: 1001}}
          onChange={setUserId}
        />
        <Select
          label="Skill"
          placeholder="Select a skill"
          required
          searchable
          data={activeSkillOptions}
          value={skillId}
          comboboxProps={{withinPortal: true, zIndex: 1001}}
          onChange={setSkillId}
        />
        <Group grow>
          <Button
            variant="outline"
            disabled={!canSubmit}
            loading={isLoading}
            onClick={handleCreateRequest}>
            Create request
          </Button>
          <Button
            disabled={!canSubmit}
            loading={isLoading}
            onClick={handleEvaluateNow}>
            Evaluate now
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
